import type { CartItemDTO } from "../../api/types.js";
import { formatPrice } from "../../lib/format.js";
import { messages } from "../../i18n/messages.js";

interface CartLineItemProps {
  item: CartItemDTO;
  disabled?: boolean;
  readOnly?: boolean;
  onIncrease?: () => void;
  onDecrease?: () => void;
  onRemove?: () => void;
}

export function CartLineItem({
  item,
  disabled = false,
  readOnly = false,
  onIncrease,
  onDecrease,
  onRemove,
}: CartLineItemProps) {
  return (
    <article className="flex gap-3 rounded-[16px] border border-black/5 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
      <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-[14px] bg-black/5">
        {item.image_url ? (
          <img src={item.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs opacity-40">
            {messages.common.noImage}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="line-clamp-2 text-sm font-medium leading-snug">{item.title}</h3>
            <p className="mt-1 text-sm font-semibold">{formatPrice(item.line_total)}</p>
            <p className="text-xs opacity-60">{messages.cart.unitPrice(formatPrice(item.unit_price))}</p>
          </div>
          {readOnly ? null : (
            <button
              type="button"
              disabled={disabled}
              onClick={onRemove}
              aria-label={messages.cart.remove}
              className="shrink-0 min-h-11 px-2 text-xs text-red-600 opacity-80 disabled:opacity-40"
            >
              {messages.cart.remove}
            </button>
          )}
        </div>
        {readOnly ? (
          <p className="text-xs opacity-60">
            {messages.product.quantity}: {item.qty}
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={disabled}
              onClick={onDecrease}
              aria-label={messages.cart.remove}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 disabled:opacity-40"
            >
              −
            </button>
            <span className="min-w-6 text-center text-sm font-medium">{item.qty}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={onIncrease}
              aria-label={messages.catalog.addToCart}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 disabled:opacity-40"
            >
              +
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
